import { SvgArrowRight } from '@/assets/ic-arrow';
import Loading from '@/components/loading/skeleton';
import SpinLoading from '@/components/loading/SpinLoading';
import { percentage } from '@/components/trading-panel/hooks/useGetPosition';
import { useCheckClosePair } from '@/hooks/useCheckClosePair';
import { useAddress } from '@/store/selector';
import { TradeData } from '@/types/trade';
import { closePosition } from '@/utils/close-position';
import { formatDisplay } from '@/utils/format-display';
import clsx from 'clsx';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SheetDetail from './sheet-detail';
import { useGetFees } from './useGetFees';
import { useTradeStore } from '@/store/trade-store';
import { MiniKit } from '@worldcoin/minikit-js';
import { toastApp } from '@/components/toast-message/sonner';

type Props = {
    item: TradeData;
    index: number;
};

export default function Item({ item, index }: Props) {
    const [isSubmit, setIsSubmit] = useState(false);
    const { t } = useTranslation();
    const { isClose } = useCheckClosePair(Number(item.pair));
    const removeOrderByIndex = useTradeStore((state) => {
        return state.removeOrderByIndex;
    });
    const address = useAddress();

    const [isOpenDetail, setIsOpenDetail] = useState(false);
    const disabled = isSubmit || isClose;

    const { data } = useGetFees(
        address,
        item.indexPrice,
        item.index,
        item.createdAt,
        item.buy,
        item.initialPosToken,
        item.leverage
    );

    const earn = useMemo(() => {
        return (
            item.net_pnl.earn -
            (Number(data?.fundingFee) || 0) -
            (Number(data?.rolloverFee) || 0)
        );
    }, [data?.fundingFee, data?.rolloverFee, item.net_pnl.earn]);

    const pnl = useMemo(() => {
        const value = (earn / item.collateralValue) * 100;
        return Math.max(value, -100);
    }, [earn, item.collateralValue]);

    const handleToggleDetail = useCallback(() => {
        setIsOpenDetail((prev) => !prev);
    }, []);

    const handleClose = useCallback(async () => {
        try {
            if (!MiniKit.isInstalled()) {
                toastApp.error('Minikit not install');
                return;
            }
            setIsSubmit(true);
            const txHash = await closePosition(item.pair, item.index, address);

            if (txHash) {
                removeOrderByIndex(index);
            }
        } finally {
            setIsSubmit(false);
        }
    }, [address, index, item.index, item.pair, removeOrderByIndex]);
    return (
        <div className="bg-[#141414] rounded-2xl p-5 mb-3">
            <div className="flex flex-row  gap-1 items-center justify-between">
                <div className="flex items-center gap-x-2">
                    <img
                        src={item.imgUrl}
                        alt={item.pairName}
                        className="w-6 h-6 rounded-full"
                    />
                    <span className="text-xl text-white">
                        {/* {item.pairName.split('USD')[0]} */}

                        {item.pairName}
                    </span>
                </div>

                <div className="flex items-center gap-x-2">
                    <p
                        className={clsx(
                            'uppercase text-[11px] leading-4 border rounded-md ml-2 px-1',
                            {
                                'text-green1 border-green1': !!item.buy,
                                '!text-red1 !border-red1': !item.buy,
                            }
                        )}
                    >
                        {item.type}
                    </p>

                    <p className="uppercase text-[11px] leading-4 text-white border-white border rounded-md px-1.5">
                        {item.leverage}x
                    </p>
                </div>
            </div>

            <Loading isLoading={item.isLoading} className="min-h-[72px] mt-3">
                <div
                    className={clsx(
                        'rounded-2xl px-2 py-3 flex flex-col justify-center items-center mt-3 relative unrealized-box',
                        {
                            'bg-transparent2': earn >= 0,
                            'bg-transparentRed2': earn < 0,
                        }
                    )}
                >
                    <p
                        className={clsx(
                            'text-left text-sm text-[#666] opacity-40 mb-1',
                            {
                                'text-green1': earn >= 0,
                                'text-red1': earn < 0,
                            }
                        )}
                    >
                        {'PNL'}: {percentage(pnl)}
                    </p>

                    <p
                        className={clsx(
                            'uppercase text-[0.8125rem] leading-4 text-left flex items-center text-base justify-start ',
                            {
                                'text-green1': earn >= 0,
                                'text-red1': earn < 0,
                            }
                        )}
                    >
                        {earn > 0 ? '+' : ''}
                        {formatDisplay(earn, {
                            decimalToShow: 2,
                        })}{' '}
                        {item.tradeCoinName}
                    </p>

                    {/* Svg Animate */}
                    <svg
                        height="100%"
                        width="100%"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <rect
                            stroke={
                                earn >= 0
                                    ? 'rgba(56, 242, 74, 0.20)'
                                    : 'rgba(255, 105, 95, 0.20)'
                            }
                            rx="8"
                            ry="8"
                            className="line"
                            height="100%"
                            width="100%"
                            strokeLinejoin="round"
                        />
                    </svg>
                </div>
            </Loading>

            <div className="flex flex-row self-stretch gap-2 items-center justify-between mt-3">
                <div className="flex flex-col gap-1">
                    <p className="text-start text-xs text-[#666]">
                        {t('Liq. Price')}
                    </p>
                    <p className="uppercase text-sm text-white text-start">
                        {formatDisplay(item.liquidationPrice, {
                            decimalToShow: item.decimalToShow,
                        })}{' '}
                    </p>
                </div>

                <div className="flex flex-col gap-1">
                    <p className="text-start  text-xs text-[#666] leading-4">
                        {t('Entry Price')}
                    </p>
                    <p className="uppercase text-sm text-white text-start">
                        {formatDisplay(Number(item.open_price), {
                            decimalToShow: item.decimalToShow,
                        })}
                    </p>
                </div>

                <div className="flex flex-col gap-1">
                    <p className="text-start text-xs text-[#666]">
                        {t('Current Price')}
                    </p>
                    <p className="uppercase text-sm text-white text-start">
                        {formatDisplay(item.price, {
                            decimalToShow: item.decimalToShow,
                        })}
                    </p>
                </div>
            </div>

            <div className="flex items-center gap-x-2 mt-4 ">
                <button
                    disabled={disabled || isSubmit}
                    className={clsx(
                        'bg-[#1C1C1C] px-2 py-3 rounded-xl text-white flex-1 flex flex-row justify-center',
                        {
                            'opacity-50': disabled,
                        }
                    )}
                    onClick={handleClose}
                >
                    {isSubmit ? (
                        <SpinLoading />
                    ) : (
                        <p className="text-center"> {t('Close')}</p>
                    )}
                </button>
                <div
                    className="bg-[#1C1C1C] px-2 py-3 rounded-xl text-white flex-1 flex justify-center"
                    onClick={handleToggleDetail}
                >
                    <SvgArrowRight />
                </div>
            </div>

            {isOpenDetail && (
                <SheetDetail
                    item={item}
                    isOpen={isOpenDetail}
                    onClose={handleToggleDetail}
                />
            )}
        </div>
    );
}
